import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type {
  PrepareQuestionnaireRequest,
  PrepareQuestionnaireResponse,
  QuestionnaireResult,
  QuestionnaireSummary,
} from '@/api/types';

export function useQuestionnaires() {
  return useQuery({
    queryKey: ['questionnaires'],
    queryFn: async () => (await api.get<Array<QuestionnaireSummary>>('/questionnaires')).data,
    // The Function has a 3h processing timeout -- this has to be poll-or-notify, not a
    // spinner, per FRONTEND_MIGRATION.md.
    refetchInterval: 5000,
  });
}

export function useQuestionnaireResult(id: string) {
  return useQuery({
    queryKey: ['questionnaire', id],
    queryFn: async () => (await api.get<QuestionnaireResult>(`/questionnaires/${id}`)).data,
    enabled: Boolean(id),
    retry: false,
  });
}

export function usePrepareQuestionnaire() {
  return useMutation({
    mutationFn: async (body: PrepareQuestionnaireRequest) =>
      (await api.post<PrepareQuestionnaireResponse>('/questionnaires/prepare', body)).data,
  });
}

type QuestionnaireUploadKind = 'questionnaire' | 'sharepoint-ids' | 'extra-document';

export async function uploadQuestionnaireFile(
  timestamp: string,
  kind: QuestionnaireUploadKind,
  body: Blob,
  contentType: string,
  fileName?: string
): Promise<void> {
  await api.put(`/questionnaires/${timestamp}/files/${kind}`, body, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Blob-Content-Type': contentType,
    },
    params: fileName ? { filename: fileName } : undefined,
  });
}

export function useCommitQuestionnaire() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (timestamp: string) =>
      (await api.post(`/questionnaires/${timestamp}/commit`)).data,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['questionnaires'] }),
  });
}

export function useDeleteQuestionnaire() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/questionnaires/${id}`)).data,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['questionnaires'] }),
  });
}

function fileNameFromContentDisposition(header: string | undefined): string {
  const match = header?.match(/filename="([^"]+)"/u);
  return match?.[1] ?? 'questionnaire.xlsx';
}

export function useDownloadExcel() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.get<Blob>(`/questionnaires/${id}/excel`, {
        responseType: 'blob',
      });
      const fileName = fileNameFromContentDisposition(
        response.headers['content-disposition']
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    },
  });
}
